/**
 * Local-First Data Repository
 * Provides persistent local state for child profiles, progress,
 * Kataku (discovered words), garden growth, and parent preferences.
 */

import {
  deleteAllPersistedData,
  exportPersistedAppState,
  hydrateAppState,
  persistAppState,
  replacePersistedAppState,
} from '../../adapters/storage/dexieStorage';

export interface ChildProfile {
  id: string;
  nickname: string;
  learningLevel: 'PAUD' | 'TK A' | 'TK B';
  avatarId: string;
  stars: number;
}

export interface DiscoveredWord {
  id: string;
  originalText: string;
  normalizedText: string;
  syllables: string[];
  confidence: 'high' | 'medium' | 'low';
  source: 'camera' | 'pelajaran' | 'manual';
  discoveredAt: string;
  mastery: 'baru' | 'sedang_dipelajari' | 'dikuasai';
  imageUrl?: string;
}

export interface GardenState {
  stage: number; // 0: Benih, 1: Kecambah, 2: Tanaman Kecil, 3: Bunga Cantik, 4: Pohon Rindang, 5: Pohon Berbuah
  waterCount: number;
  totalPoints: number;
  unlockedBadges: string[];
}

export interface ParentSettings {
  speechRate: 'normal' | 'slow';
  speechEnabled: boolean;
  soundEffects: boolean;
  retainDiscoveryImages: boolean;
  parentPin: string;
}

export interface LearningProgress {
  lettersFamiliar: string[];
  syllablesFamiliar: string[];
  wordsMastered: string[];
  learningDays: number;
  learningMinutes: number;
  lastPracticedDate: string;
}

export interface AppState {
  profile: ChildProfile;
  discoveredWords: DiscoveredWord[];
  garden: GardenState;
  progress: LearningProgress;
  settings: ParentSettings;
}

export interface ProfileRepositoryContract {
  getProfile(): ChildProfile;
  updateProfile(updates: Partial<ChildProfile>): void;
}

export interface ProgressRepositoryContract {
  getProgress(): LearningProgress;
  recordLetterLearned(letter: string): void;
  recordSyllableLearned(syllable: string): void;
  recordWordMastered(word: string): void;
  resetProgress(): Promise<void>;
}

export interface DiscoveredWordsRepositoryContract {
  getDiscoveredWords(): DiscoveredWord[];
  addDiscoveredWord(word: DiscoveredWord): boolean;
  deleteDiscoveredWord(id: string): void;
}

export interface GardenRepositoryContract {
  getGarden(): GardenState;
}

export interface SettingsRepositoryContract {
  getSettings(): ParentSettings;
  updateSettings(settings: Partial<ParentSettings>): void;
}

export interface LearningSessionRepositoryContract {
  getCurrentState(): AppState;
  exportData(): Promise<string>;
  deleteAllData(): Promise<void>;
}

function withoutDiscoveryImage(word: DiscoveredWord): DiscoveredWord {
  const { imageUrl: _imageUrl, ...wordWithoutImage } = word;
  return wordWithoutImage;
}

function cloneDefaultState(): AppState {
  return {
    ...DEFAULT_STATE,
    profile: { ...DEFAULT_STATE.profile },
    discoveredWords: DEFAULT_STATE.discoveredWords.map(word => ({ ...word })),
    garden: { ...DEFAULT_STATE.garden, unlockedBadges: [...DEFAULT_STATE.garden.unlockedBadges] },
    progress: {
      ...DEFAULT_STATE.progress,
      lettersFamiliar: [...DEFAULT_STATE.progress.lettersFamiliar],
      syllablesFamiliar: [...DEFAULT_STATE.progress.syllablesFamiliar],
      wordsMastered: [...DEFAULT_STATE.progress.wordsMastered],
    },
    settings: { ...DEFAULT_STATE.settings },
  };
}

const DEFAULT_STATE: AppState = {
  profile: {
    id: 'child-1',
    nickname: 'Mila',
    learningLevel: 'TK A',
    avatarId: 'rara-buddy',
    stars: 12,
  },
  discoveredWords: [
    {
      id: 'disc-1',
      originalText: 'JALAN',
      normalizedText: 'jalan',
      syllables: ['JA', 'LAN'],
      confidence: 'high',
      source: 'camera',
      discoveredAt: new Date(Date.now() - 86400000).toISOString(),
      mastery: 'sedang_dipelajari',
    },
    {
      id: 'disc-2',
      originalText: 'BUKU',
      normalizedText: 'buku',
      syllables: ['BU', 'KU'],
      confidence: 'high',
      source: 'camera',
      discoveredAt: new Date(Date.now() - 43200000).toISOString(),
      mastery: 'dikuasai',
    },
    {
      id: 'disc-3',
      originalText: 'BAJU',
      normalizedText: 'baju',
      syllables: ['BA', 'JU'],
      confidence: 'high',
      source: 'pelajaran',
      discoveredAt: new Date().toISOString(),
      mastery: 'dikuasai',
    },
  ],
  garden: {
    stage: 2, // Tanaman Kecil
    waterCount: 6,
    totalPoints: 24,
    unlockedBadges: ['Kenal 5 Huruf', 'Penemu Kata Pertama', 'Sahabat Rara'],
  },
  progress: {
    lettersFamiliar: ['A', 'B', 'C', 'D', 'E', 'I', 'M', 'N', 'S', 'T'],
    syllablesFamiliar: ['BA', 'BI', 'BU', 'BO', 'MA', 'MI', 'SA', 'SU'],
    wordsMastered: ['BAJU', 'BOLA', 'BUKU', 'MATA'],
    learningDays: 4,
    learningMinutes: 48,
    lastPracticedDate: new Date().toISOString().slice(0, 10),
  },
  settings: {
    speechRate: 'normal',
    speechEnabled: true,
    soundEffects: true,
    retainDiscoveryImages: true,
    parentPin: '1234',
  },
};

export class AppRepository implements
  ProfileRepositoryContract,
  ProgressRepositoryContract,
  DiscoveredWordsRepositoryContract,
  GardenRepositoryContract,
  SettingsRepositoryContract,
  LearningSessionRepositoryContract {
  private state: AppState;
  private listeners: Array<(state: AppState) => void> = [];

  constructor() {
    this.state = DEFAULT_STATE;
    void this.hydrateFromIndexedDb();
  }

  private async hydrateFromIndexedDb() {
    const hydratedState = await hydrateAppState(DEFAULT_STATE);
    if (hydratedState === this.state) return;
    const learningLevel = (hydratedState.profile.learningLevel as string) === 'Pemula'
      ? 'PAUD'
      : hydratedState.profile.learningLevel;
    this.state = learningLevel === hydratedState.profile.learningLevel
      ? hydratedState
      : {
          ...hydratedState,
          profile: { ...hydratedState.profile, learningLevel },
        };
    this.notify();
  }

  private saveState() {
    void persistAppState(this.state);
    try {
      this.notify();
    } catch {
      // Listener failures must not break the local write path.
    }
  }

  public subscribe(listener: (state: AppState) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notify() {
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }

  public getState(): AppState {
    return this.state;
  }

  public getCurrentState(): AppState {
    return this.getState();
  }

  public getProfile(): ChildProfile {
    return this.state.profile;
  }

  public getProgress(): LearningProgress {
    return this.state.progress;
  }

  public getDiscoveredWords(): DiscoveredWord[] {
    return this.state.discoveredWords;
  }

  public getGarden(): GardenState {
    return this.state.garden;
  }

  public getSettings(): ParentSettings {
    return this.state.settings;
  }

  public updateProfile(updates: Partial<ChildProfile>) {
    this.state = {
      ...this.state,
      profile: { ...this.state.profile, ...updates },
    };
    this.saveState();
  }

  public addDiscoveredWord(word: DiscoveredWord): boolean {
    const existing = this.state.discoveredWords.find(
      w => w.normalizedText === word.normalizedText
    );
    if (existing) {
      return false; // already present
    }
    const storedWord = this.state.settings.retainDiscoveryImages
      ? word
      : withoutDiscoveryImage(word);
    this.state = {
      ...this.state,
      discoveredWords: [storedWord, ...this.state.discoveredWords],
      profile: {
        ...this.state.profile,
        stars: this.state.profile.stars + 1,
      },
      garden: {
        ...this.state.garden,
        totalPoints: this.state.garden.totalPoints + 3,
        waterCount: this.state.garden.waterCount + 1,
        stage: Math.min(5, Math.floor((this.state.garden.totalPoints + 3) / 10)),
      },
    };
    this.saveState();
    return true;
  }

  public deleteDiscoveredWord(id: string) {
    this.state = {
      ...this.state,
      discoveredWords: this.state.discoveredWords.filter(w => w.id !== id),
    };
    this.saveState();
  }

  public recordLetterLearned(letter: string) {
    const l = letter.toUpperCase();
    if (!this.state.progress.lettersFamiliar.includes(l)) {
      this.state = {
        ...this.state,
        progress: {
          ...this.state.progress,
          lettersFamiliar: [...this.state.progress.lettersFamiliar, l],
        },
        profile: {
          ...this.state.profile,
          stars: this.state.profile.stars + 1,
        },
        garden: {
          ...this.state.garden,
          waterCount: this.state.garden.waterCount + 1,
          totalPoints: this.state.garden.totalPoints + 2,
          stage: Math.min(5, Math.floor((this.state.garden.totalPoints + 2) / 10)),
        },
      };
      this.saveState();
    }
  }

  public recordSyllableLearned(syllable: string) {
    const s = syllable.toUpperCase();
    if (!this.state.progress.syllablesFamiliar.includes(s)) {
      this.state = {
        ...this.state,
        progress: {
          ...this.state.progress,
          syllablesFamiliar: [...this.state.progress.syllablesFamiliar, s],
        },
        profile: {
          ...this.state.profile,
          stars: this.state.profile.stars + 1,
        },
        garden: {
          ...this.state.garden,
          waterCount: this.state.garden.waterCount + 1,
          totalPoints: this.state.garden.totalPoints + 2,
          stage: Math.min(5, Math.floor((this.state.garden.totalPoints + 2) / 10)),
        },
      };
      this.saveState();
    }
  }

  public recordWordMastered(word: string) {
    const w = word.toUpperCase();
    if (!this.state.progress.wordsMastered.includes(w)) {
      this.state = {
        ...this.state,
        progress: {
          ...this.state.progress,
          wordsMastered: [...this.state.progress.wordsMastered, w],
        },
        profile: {
          ...this.state.profile,
          stars: this.state.profile.stars + 2,
        },
        garden: {
          ...this.state.garden,
          waterCount: this.state.garden.waterCount + 2,
          totalPoints: this.state.garden.totalPoints + 5,
          stage: Math.min(5, Math.floor((this.state.garden.totalPoints + 5) / 10)),
        },
      };
      this.saveState();
    }
  }

  public updateSettings(settings: Partial<ParentSettings>) {
    const nextSettings = { ...this.state.settings, ...settings };
    this.state = {
      ...this.state,
      settings: nextSettings,
      discoveredWords: nextSettings.retainDiscoveryImages
        ? this.state.discoveredWords
        : this.state.discoveredWords.map(withoutDiscoveryImage),
    };
    this.saveState();
  }

  public async resetProgress() {
    this.state = {
      ...cloneDefaultState(),
      profile: {
        ...cloneDefaultState().profile,
        nickname: this.state.profile.nickname,
      },
    };
    await replacePersistedAppState(this.state);
    this.notify();
  }

  public async deleteAllData() {
    this.state = cloneDefaultState();
    await deleteAllPersistedData();
    this.notify();
  }

  public async exportData(): Promise<string> {
    return exportPersistedAppState(this.state);
  }
}

export const appRepository = new AppRepository();
